// ResourcesSection.jsx - Updated to handle resourceToView prop
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Grid,
    CircularProgress,
    Skeleton,
    Fade,
    Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme, useMediaQuery } from '@mui/material';
import DeskIcon from '@mui/icons-material/Desk';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import BusinessIcon from '@mui/icons-material/Business';
import { useProfile } from 'context';

// Import components
import MeetingRoomCard from './meetingRoomCard';
import MeetingRoomDetailModal from './meetingRoomDetailModal';
import HotDeskCard from './hotDeskCard';
import HotDeskDetailModal from './hotDeskDetailModal';

// Styled components
const ResourcesScrollContainer = styled(Box)(({ theme }) => ({
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(3),
    '&::-webkit-scrollbar': {
        height: '6px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: theme.palette.grey[100],
        borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.grey[400],
        borderRadius: '10px',
    }
}));

// Custom styled tabs for resource types
const ResourceTabs = styled(Tabs)(({ theme }) => ({
    minHeight: '48px',
    '& .MuiTabs-flexContainer': {
        overflowX: 'auto',
        scrollbarWidth: 'none', /* Firefox */
        '&::-webkit-scrollbar': {
            display: 'none', /* Chrome, Safari, Edge */
        },
    },
    '& .MuiTab-root': {
        minWidth: 'auto',
        padding: theme.spacing(1, 2),
        fontSize: '0.9rem',
        textTransform: 'none',
        fontWeight: 500,
        minHeight: '40px',
    }
}));

// Work Area Card
const WorkAreaCard = styled(Box)(({ theme }) => ({
    width: 250,
    height: 180,
    margin: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[4],
    }
}));

// Function to get icon for resource type
const getResourceIcon = (resourceType) => {
    switch (resourceType) {
        case 'hot_desk':
        case 'HD':
        case 'coworking':
            return <DeskIcon />;
        case 'meeting_room':
        case 'MR':
            return <MeetingRoomIcon />;
        case 'private_office':
        case 'ST':
            return <BusinessIcon />;
        default:
            return <DeskIcon />;
    }
};

const ResourcesSection = ({
    floors,
    venueSlug,
    selectedDate,
    venueResources = null,
    onBookTimeSlot = null,
    loadingResources = false,
    onDateChange = null,
    initialBookingType = null,
    onBookingTypeChange = null,
    externalSelectedResource = null,
    onModalClose = () => { },
    resourceToView = null,
    onSelectResource = null,
}) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedMeetingRoom, setSelectedMeetingRoom] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedHotDeskArea, setSelectedHotDeskArea] = useState(null);
    const [hotDeskModalOpen, setHotDeskModalOpen] = useState(false);
    const [processedResourceId, setProcessedResourceId] = useState(null);
    // Add a new state to track the last viewed resource
    const [lastViewedResourceId, setLastViewedResourceId] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { setBookingType, bookingType } = useProfile();

    // Extract different resource types from floors data
    const workAreasWithHotDesks = extractHotDesks(floors);
    const meetingRoomsFromFloors = extractMeetingRooms(floors);
    const privateOffices = extractPrivateOffices(floors);

    // Determine which tabs to show based on available resources
    const tabs = [];
    if (workAreasWithHotDesks.length > 0) tabs.push('Hot Desks');

    // Check for meeting rooms from both data sources
    if (meetingRoomsFromFloors.length > 0 || (venueResources && venueResources.length > 0)) {
        tabs.push('Meeting Rooms');
    }

    if (privateOffices.length > 0) tabs.push('Private Offices');

    // New useEffect to handle resourceToView prop
    useEffect(() => {
        if (!resourceToView) return;

        // Create a unique identifier for this resource
        const resourceIdentifier = resourceToView._timestamp ||
            resourceToView.id ||
            resourceToView.slug;

        // Skip if we've already processed this exact resource
        if (resourceIdentifier === lastViewedResourceId) return;


        // Determine resource type and show appropriate modal
        const isMeetingRoom = resourceToView.type === 'MR' ||
            resourceToView.capacity !== undefined ||
            resourceToView.hourly_rate !== undefined;

        const isHotDesk = resourceToView.type === 'HD' ||
            resourceToView.hotDesksCount !== undefined ||
            resourceToView.workAreaSlug !== undefined;

        if (isMeetingRoom) {
            // For meeting rooms
            const meetingRoomsToCheck = venueResources || meetingRoomsFromFloors;
            const foundRoom = meetingRoomsToCheck.find(room =>
                (room.id && room.id === resourceToView.id) ||
                (room.slug && room.slug === resourceToView.slug)
            ) || resourceToView; // Use the resource if we can't find a match

            // Switch to meeting rooms tab first
            const mrTabIndex = tabs.indexOf('Meeting Rooms');
            if (mrTabIndex !== -1) {
                setSelectedTab(mrTabIndex);
            }

            // Then show the modal with this resource
            setSelectedMeetingRoom(foundRoom);
            setModalOpen(true);
        }
        else if (isHotDesk) {
            // For hot desks
            const foundArea = workAreasWithHotDesks.find(area =>
                (area.workAreaId && area.workAreaId === resourceToView.workAreaId) ||
                (area.slug && area.slug === resourceToView.slug) ||
                (area.workAreaSlug && area.workAreaSlug === resourceToView.workAreaSlug)
            ) || resourceToView; // Use the resource if we can't find a match

            // Switch to hot desks tab first
            const hdTabIndex = tabs.indexOf('Hot Desks');
            if (hdTabIndex !== -1) {
                setSelectedTab(hdTabIndex);
            }

            // Then show the modal with this resource
            setSelectedHotDeskArea(foundArea);
            setHotDeskModalOpen(true);
        }

        // Mark this resource as viewed
        setLastViewedResourceId(resourceIdentifier);
    }, [resourceToView, venueResources, meetingRoomsFromFloors, workAreasWithHotDesks, tabs, lastViewedResourceId]);

    // Handle external resource selection
    useEffect(() => {
        if (!externalSelectedResource) return;

        // Create a unique identifier for this resource to prevent processing the same resource multiple times
        const resourceIdentifier = externalSelectedResource._timestamp ||
            externalSelectedResource.id ||
            externalSelectedResource.slug;

        // Skip if we've already processed this exact resource
        if (resourceIdentifier === processedResourceId) return;


        // Determine resource type and show appropriate modal
        const isMeetingRoom = externalSelectedResource.type === 'MR' ||
            externalSelectedResource.capacity !== undefined ||
            externalSelectedResource.hourly_rate !== undefined;

        const isHotDesk = externalSelectedResource.type === 'HD' ||
            externalSelectedResource.hotDesksCount !== undefined ||
            externalSelectedResource.workAreaSlug !== undefined;

        if (isMeetingRoom) {
            // For meeting rooms
            const meetingRoomsToCheck = venueResources || meetingRoomsFromFloors;
            const foundRoom = meetingRoomsToCheck.find(room =>
                (room.id && room.id === externalSelectedResource.id) ||
                (room.slug && room.slug === externalSelectedResource.slug)
            ) || externalSelectedResource; // Use the external resource if we can't find a match

            // Switch to meeting rooms tab first
            const mrTabIndex = tabs.indexOf('Meeting Rooms');
            if (mrTabIndex !== -1) {
                setSelectedTab(mrTabIndex);
            }

            // Then show the modal with this resource
            setSelectedMeetingRoom(foundRoom);
            setModalOpen(true);
        }
        else if (isHotDesk) {
            // For hot desks
            const foundArea = workAreasWithHotDesks.find(area =>
                (area.workAreaId && area.workAreaId === externalSelectedResource.workAreaId) ||
                (area.slug && area.slug === externalSelectedResource.slug) ||
                (area.workAreaSlug && area.workAreaSlug === externalSelectedResource.workAreaSlug)
            ) || externalSelectedResource; // Use the external resource if we can't find a match

            // Switch to hot desks tab first
            const hdTabIndex = tabs.indexOf('Hot Desks');
            if (hdTabIndex !== -1) {
                setSelectedTab(hdTabIndex);
            }

            // Then show the modal with this resource
            setSelectedHotDeskArea(foundArea);
            setHotDeskModalOpen(true);
        }

        // Mark this resource as processed
        setProcessedResourceId(resourceIdentifier);
    }, [externalSelectedResource, venueResources, meetingRoomsFromFloors, workAreasWithHotDesks, tabs, processedResourceId]);

    useEffect(() => {
        if (initialBookingType && tabs.length > 0) {
            // Map booking type to tab index
            let tabIndex = 0;

            switch (initialBookingType.toUpperCase()) {
                case 'HD': // Hot Desk
                    tabIndex = tabs.indexOf('Hot Desks');
                    break;
                case 'MR': // Meeting Room
                    tabIndex = tabs.indexOf('Meeting Rooms');
                    break;
                case 'ST': // Private Office
                    tabIndex = tabs.indexOf('Private Offices');
                    break;
            }

            // Only set if the tab exists
            if (tabIndex !== -1) {
                setSelectedTab(tabIndex);

                // Also update the context booking type to match
                setBookingType(initialBookingType.toUpperCase());
            }
        }
    }, [initialBookingType, tabs, setBookingType]);

    // If no resources, don't show the section
    if (tabs.length === 0) return null;

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);

        // Map tab names to the correct booking type values
        const tabName = tabs[newValue];
        let bookingTypeValue;

        switch (tabName) {
            case 'Hot Desks':
                bookingTypeValue = 'HD';
                break;
            case 'Meeting Rooms':
                bookingTypeValue = 'MR';
                break;
            case 'Private Offices':
                bookingTypeValue = 'ST';
                break;
            default:
                bookingTypeValue = 'HD';
        }

        // Update the context
        setBookingType(bookingTypeValue);

        // IMPORTANT: Notify parent component about the change
        if (typeof onBookingTypeChange === 'function') {
            onBookingTypeChange(bookingTypeValue);
        }
    };

    const handleOpenModal = (room) => {
        setSelectedMeetingRoom(room);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        onModalClose();
    };

    const handleSelectRoom = (room) => {
        // Pass selected room to parent component (VenueSingle)
        if (onSelectResource) {
            // Add a timestamp to ensure the reference changes
            const resourceWithTimestamp = {
                ...room,
                _timestamp: Date.now() // Add timestamp for reference tracking
            };
            onSelectResource(resourceWithTimestamp);
        }
        handleCloseModal();
        setSelectedMeetingRoom(room);
    };

    const handleSelectHotDeskArea = (area) => {
        // Pass selected area to parent component (VenueSingle)
        if (onSelectResource) {
            // Add a timestamp to ensure the reference changes
            const areaWithTimestamp = {
                ...area,
                _timestamp: Date.now() // Add timestamp for reference tracking
            };
            onSelectResource(areaWithTimestamp);
        }
        handleCloseHotDeskModal();
    };

    const handleOpenHotDeskModal = (area) => {
        setSelectedHotDeskArea(area);
        setHotDeskModalOpen(true);
    };

    const handleCloseHotDeskModal = () => {
        setHotDeskModalOpen(false);
        onModalClose();
    };

    // Handle date change from modal
    const handleDateChange = (newDate) => {
        if (onDateChange) {
            onDateChange(newDate);
        }
    };

    // Use the API data when available, otherwise use data from floors
    const meetingRoomsToDisplay = venueResources || meetingRoomsFromFloors.map(room => ({
        ...room,
        // Generate mock hourly slots if real data not available
        hourly_slots: Array.from({ length: 18 }, (_, i) => ({
            start_time: `${Math.floor(i / 2) + 9}:${i % 2 === 0 ? '00' : '30'}`,
            end_time: `${Math.floor((i + 1) / 2) + 9}:${(i + 1) % 2 === 0 ? '00' : '30'}`,
            is_available: Math.random() > 0.3,
            duration_minutes: 30,
            is_full_slot: true,
            price: room.hourlyPrice / 2 || 25
        }))
    }));

    // Render loading skeletons for meeting room cards
    const renderMeetingRoomSkeletons = () => {
        return Array(4).fill(0).map((_, index) => (
            <Grid item xs={12} sm={12} md={6} key={`skeleton-${index}`}>
                <Box sx={{ borderRadius: 1, overflow: 'hidden', boxShadow: 1, height: 320 }}>
                    <Skeleton variant="rectangular" height={180} animation="wave" />
                    <Box sx={{ p: 2 }}>
                        <Skeleton variant="text" height={30} width="70%" animation="wave" />
                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                            <Skeleton variant="rectangular" height={24} width={100} animation="wave" />
                            <Skeleton variant="rectangular" height={24} width={80} animation="wave" />
                        </Box>
                        <Skeleton variant="rectangular" height={12} width="100%" sx={{ mt: 2 }} animation="wave" />
                        <Skeleton variant="rectangular" height={12} width="100%" sx={{ mt: 1 }} animation="wave" />
                    </Box>
                </Box>
            </Grid>
        ));
    };

    return (
        <ResourcesScrollContainer>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <ResourceTabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="resource types"
                >
                    {tabs.map((tab) => (
                        <Tab
                            key={tab}
                            label={tab}
                            icon={getResourceIcon(
                                tab === 'Hot Desks' ? 'hot_desk' :
                                    tab === 'Meeting Rooms' ? 'meeting_room' :
                                        'private_office'
                            )}
                            iconPosition="start"
                        />
                    ))}
                </ResourceTabs>
            </Box>

            {/* Hot Desks Tab Panel */}
            {selectedTab === tabs.indexOf('Hot Desks') && (
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        {workAreasWithHotDesks.map(area => (
                            <Grid item xs={12} sm={12} md={6} key={area.workAreaId}>
                                <HotDeskCard
                                    area={area}
                                    onClick={handleOpenHotDeskModal}
                                    isMobile={isMobile}
                                    theme={theme}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Meeting Rooms Tab Panel */}
            {selectedTab === tabs.indexOf('Meeting Rooms') && (
                <Box sx={{ mt: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }}>

                        {loadingResources && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="caption" color="text.secondary">
                                    Loading availability...
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Always render the grid, just change what's inside based on loading state */}
                    <Grid container spacing={2}>
                        {loadingResources ? (
                            // Show skeletons while loading
                            renderMeetingRoomSkeletons()
                        ) : meetingRoomsToDisplay && meetingRoomsToDisplay.length > 0 ? (
                            // Show actual data when loaded
                            meetingRoomsToDisplay.map((room) => (
                                <Grid item xs={12} sm={12} md={6} key={room.slug || room.id}>
                                    <MeetingRoomCard
                                        room={room}
                                        onClick={handleOpenModal}
                                        isMobile={isMobile}
                                        theme={theme}
                                        onSelectRoom={handleSelectRoom}
                                        isSelected={selectedMeetingRoom?.slug === room.slug}
                                    />
                                </Grid>
                            ))
                        ) : (
                            // Show "no rooms available" message when there's no data
                            <Grid item xs={12}>
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No meeting rooms available for this date.
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}

            {/* Private Offices Tab Panel */}
            {selectedTab === tabs.indexOf('Private Offices') && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                        Private offices
                    </Typography>
                    <Box sx={{ display: 'flex', overflowX: 'auto', py: 1 }}>
                        {privateOffices.map(office => (
                            <WorkAreaCard
                                key={office.id}
                                sx={{
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    boxShadow: 1,
                                    bgcolor: 'background.paper'
                                }}
                            >
                                <Box
                                    sx={{
                                        height: 120,
                                        backgroundImage: office.images?.length > 0
                                            ? `url(${office.images[0].url})`
                                            : 'none',
                                        backgroundColor: 'grey.200',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                />
                                <Box sx={{ p: 2, flexGrow: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">{office.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">{office.floorName}</Typography>
                                </Box>
                            </WorkAreaCard>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Meeting Room Details Modal */}
            <MeetingRoomDetailModal
                open={modalOpen}
                onClose={handleCloseModal}
                room={selectedMeetingRoom}
                onSelectRoom={handleSelectRoom}
                theme={theme}
            />
            {/* Hot Desk Detail Modal */}
            <HotDeskDetailModal
                open={hotDeskModalOpen}
                onClose={handleCloseHotDeskModal}
                area={selectedHotDeskArea}
                onSelectArea={handleSelectHotDeskArea}
                theme={theme}
            />
        </ResourcesScrollContainer>
    );
};

// Helper functions to extract resources from floors data
const extractHotDesks = (floors) => {
    const workAreasWithHotDesks = [];

    floors?.forEach(floor => {
        floor.work_areas?.forEach(workArea => {
            // Skip inactive work areas (status = 'IN') for hot desks
            if (workArea.status === 'IN') {
                return;
            }

            if (workArea.hot_desks?.length > 0) {
                workAreasWithHotDesks.push({
                    workAreaName: workArea.name,
                    workAreaId: workArea.id,
                    floorName: floor.name,
                    hotDesksCount: workArea.hot_desks.length,
                    images: workArea.images || [],
                    slug: workArea.slug,
                    services: workArea.services,
                    description: workArea.description,
                });
            }
        });
    });

    return workAreasWithHotDesks;
};

const extractMeetingRooms = (floors) => {
    const meetingRooms = [];

    floors?.forEach(floor => {
        floor.work_areas?.forEach(workArea => {
            if (workArea.meeting_rooms?.length > 0) {
                // Filter out meeting rooms with status "IN" (inactive)
                const activeRooms = workArea.meeting_rooms.filter(room => room.status !== "IN");

                meetingRooms.push(...activeRooms.map(room => ({
                    ...room,
                    workAreaName: workArea.name,
                    floorName: floor.name,
                    hourlyPrice: room.hourlyPrice || 25
                })));
            }
        });
    });

    return meetingRooms;
};

const extractPrivateOffices = (floors) => {
    const privateOffices = [];

    floors?.forEach(floor => {
        floor.work_areas?.forEach(workArea => {
            if (workArea.dedicated_desks?.length > 0) {
                privateOffices.push(...workArea.dedicated_desks.map(office => ({
                    ...office,
                    workAreaName: workArea.name,
                    floorName: floor.name
                })));
            }
        });
    });

    return privateOffices;
};

export default ResourcesSection;